import type { Chart, ChartType, TooltipModel } from 'chart.js';
import { useCallback, useRef, useState } from 'react';

type UseChartTooltipOptions = {
  chart: Chart | null;
  interactive?: boolean;
};

export function useChartTooltip({
  chart,
  interactive = true,
}: UseChartTooltipOptions) {
  const [tooltipData, setTooltipData] = useState<TooltipModel<any> | null>(
    null,
  );
  const tooltipYRef = useRef<number | undefined>(undefined);
  const tooltipSignatureRef = useRef<string>('hidden');

  const chartTooltip = useCallback(
    <T extends ChartType>(tooltipModel: {
      tooltip: TooltipModel<T>;
      replay?: boolean;
    }) => {
      const tooltip = tooltipModel.tooltip;
      const canvas = chart?.canvas ?? tooltip.chart.canvas;

      if (interactive && canvas) {
        canvas.style.cursor = 'pointer';
      }

      if (tooltip.opacity !== 1) {
        tooltipYRef.current = undefined;
        if (tooltipSignatureRef.current !== 'hidden') {
          tooltipSignatureRef.current = 'hidden';
          setTooltipData((prev) => (prev === null ? prev : null));
        }
        return;
      }

      const top = tooltip.y;
      if (tooltipYRef.current !== top) {
        const dataPoint = tooltip.dataPoints?.[0];
        const signature = [
          Math.round(tooltip.caretX ?? 0),
          Math.round(tooltip.caretY ?? 0),
          dataPoint?.datasetIndex ?? -1,
          dataPoint?.dataIndex ?? -1,
        ].join(':');
        if (tooltipSignatureRef.current === signature) return;

        tooltipSignatureRef.current = signature;
        tooltipYRef.current = top;
        setTooltipData(tooltip);
      }
    },
    [chart, interactive],
  );

  const hideTooltip = useCallback(() => setTooltipData(null), []);

  return {
    chartTooltip,
    tooltipData,
    hideTooltip,
  };
}
